import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/guides/palworld-mobile-strategy-hub";
const TITLE = "Palworld Mobile Release Date & Phone Play Guide";
const DESCRIPTION =
  "Palworld mobile release date status, how to play Palworld online on a phone today, and mobile pal catch rate calculators for sphere odds on the go.";

const FAQ: FaqItem[] = [
  {
    q: "Is there a Palworld mobile release date?",
    a: "No native iOS or Android version of Palworld has been dated. Pocketpair has not announced a mobile build, so any date circulating online is speculation rather than an official release date.",
  },
  {
    q: "How do you play Palworld online on a phone right now?",
    a: "Two routes work today: stream the PC copy over your own network with Steam Link, or run it through a cloud gaming service that lists Palworld. Both need a controller and a stable connection — the game itself still runs on the PC or the cloud host, not the handset.",
  },
  {
    q: "Do mobile catch rate calculators work in a phone browser?",
    a: "Yes. Our sphere odds tools are browser-native and touch-sized, so you can check a catch chance on a phone while the game runs on another screen.",
  },
  {
    q: "Does multiplayer work when streaming to a phone?",
    a: "Multiplayer runs exactly as it does on the host machine, since the phone only receives video and sends input. Dedicated server settings are unchanged.",
  },
];

export const Route = createFileRoute("/guides/palworld-mobile-strategy-hub")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld Guides", "/guides"],
        ["Palworld mobile strategy hub", "/guides/palworld-mobile-strategy-hub"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: MobileStrategyHubPage,
});

function MobileStrategyHubPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld · platforms</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          How to Play Palworld Online on iOS and Android Mobile Devices
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Palworld has no announced native mobile version, so there is no confirmed Palworld mobile release date. What
          does work today is streaming: Steam Link over your own network, or a cloud gaming service that carries the
          game. Your phone becomes the screen and controller while the PC or cloud host runs the world.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Tracking the verified Palworld mobile release date</h2>
        <p>
          Every mobile date floating around community posts traces back to speculation, not a Pocketpair announcement.
          We watch the official channels and update this page the moment a real platform commitment appears — including
          any test or region-limited rollout, which is usually how a mobile build would surface first.
        </p>
        <p>
          Until then, treat "Palworld mobile" as streaming. Wired-in latency and a controller matter far more than the
          handset itself; a mid-range phone streams the game as well as a flagship does.
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Playing and catching on a small screen</h2>
        <p>
          Streaming shrinks the HUD, so the numbers you would normally read off the capture prompt get hard to trust.
          Keep the odds in a second tab instead: our{" "}
          <Link to="/pal-catch" className="text-primary hover:underline">
            mobile pal catch rate calculators
          </Link>{" "}
          run in the browser and show real-time sphere odds without leaving the stream.
        </p>
        <ul className="space-y-2">
          <li>
            <Link to="/capture-rates" className="text-primary hover:underline">
              Palworld capture rate table
            </Link>{" "}
            — base rates per Pal.
          </li>
          <li>
            <Link
              to="/guides/$slug"
              params={{ slug: "palworld-platforms" }}
              className="text-primary hover:underline"
            >
              Crossplay and dedicated server platform guide
            </Link>
          </li>
          <li>
            <Link to="/breeding" className="text-primary hover:underline">
              Palworld breeding calculator and data
            </Link>
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld on mobile FAQ" />
    </div>
  );
}
